import styled from "styled-components";
import { useState, useRef } from "react";
import { gql, useQuery } from "@apollo/client";
import { captureException } from "@sentry/react";

import * as css from "styles/CssVariables.js";
import TransactionList from "./TransactionList";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import ContextMenu from "components/menu/ContextMenu";
import SectionTitle from "components/text/SectionTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";

import filterIcon from "assets/img/icons/icons8-filter.svg";
import filterWhiteIcon from "assets/img/icons/icons8-filter-white.svg";

const Header = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const HeaderIcons = styled.div`
  margin-bottom: 10px;

  display: flex;
  align-items: center;
  gap: ${css.BUTTONS_GAP_SMALL};
`;

const MenuWrap = styled.div`
  position: relative;
`;

const MenuIconBox = styled.div`
  position: relative;
`;

const ActiveStatusDot = styled.div`
  position: absolute;
  top: 0;
  left: 25px;

  z-index: 100;

  &:hover {
    cursor: pointer;
  }
`;

const FilterMenu = styled.div`
  position: absolute;
  top: 42px;
  right: 0;
  z-index: 10;

  transition: 0.2s;

  opacity: ${(props) => (props.opened ? 100 : 0)};
  pointer-events: ${(props) => (props.opened ? "auto" : "none")};
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const OptionText = styled.div``;

const StatusDot = styled.div`
  width: 12px;
  min-width: 12px;
  height: 12px;

  border-radius: 50%;

  background: ${css.BRAND_BLUSH};
`;

export default () => {
  const menuIconRef = useRef();

  const [page, setPage] = useState(1);
  const [filterMenuOpened, setFilterMenuOpened] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);

  // Fetch data
  const GET_TRANSACTION_LIST = gql`
    query GetTransactionList($page: Int!, $type: String) {
      transactionList(page: $page, type: $type) {
        transactions {
          id
          created_at
          type
          amount
          data
        }
        numOfResults
        croppedData
      }
    }
  `;

  const { data, error } = useQuery(GET_TRANSACTION_LIST, {
    variables: {
      page,
      type: typeFilter,
    },
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("fetching transaction list error", error);
    captureException(error);
  }

  //console.log({ data });

  const limit = 10;

  if (data) {
    if (data.transactionList.numOfResults > 0)
      if (page > Math.ceil(data.transactionList.numOfResults / limit))
        setPage(1);
  }

  const paginate = (where) => {
    if (where === "previous") setPage((prevState) => prevState - 1);
    if (where === "next") setPage((prevState) => prevState + 1);
  };

  // handle Transactions filter
  const onFilterMenuClick = () => {
    setFilterMenuOpened((prevState) => !prevState);
  };

  const closeFilterMenu = () => {
    setFilterMenuOpened(false);
  };

  const onFilterSelected = (option) => {
    setTypeFilter(option);
    setPage(1);

    setFilterMenuOpened(false);
  };

  return (
    <>
      <Header>
        <SectionTitle marginBottom="0">Coins Transactions</SectionTitle>
        <HeaderIcons>
          <MenuWrap>
            <MenuIconBox ref={menuIconRef}>
              <RoundActionIcon
                icon={filterMenuOpened ? filterWhiteIcon : filterIcon}
                iconAlt="Filter menu"
                onClick={onFilterMenuClick}
                iconSize="16px"
              />
              {typeFilter && (
                <ActiveStatusDot onClick={onFilterMenuClick}>
                  <StatusDot />
                </ActiveStatusDot>
              )}
            </MenuIconBox>
            <FilterMenu opened={filterMenuOpened ? 1 : 0}>
              <ContextMenu
                items={[
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>All transactions</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected(null),
                    selected: !typeFilter,
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Impressions</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("impressions"),
                    selected: typeFilter === "impressions",
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Found coins</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("found"),
                    selected: typeFilter === "found",
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Referral rewards</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("referralRewards"),
                    selected: typeFilter === "referralRewards",
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Purchases</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("purchases"),
                    selected: typeFilter === "purchases",
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Ad plan coins</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("adPlanCoins"),
                    selected: typeFilter === "adPlanCoins",
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Conversions</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("conversions"),
                    selected: typeFilter === "conversions",
                  },
                  {
                    icon: null,
                    label: (
                      <FilterOption>
                        <OptionText>Bonuses</OptionText>
                      </FilterOption>
                    ),
                    onClick: () => onFilterSelected("bonuses"),
                    selected: typeFilter === "bonuses",
                  },
                ]}
                menuIconRef={menuIconRef}
                closeMenu={closeFilterMenu}
                alignItems="flex-end"
              />
            </FilterMenu>
          </MenuWrap>
        </HeaderIcons>
      </Header>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="435px" />
        </LoadingBox>
      )}
      {data && (
        <TransactionList
          data={data}
          page={page}
          limit={limit}
          paginate={paginate}
        />
      )}
    </>
  );
};
